import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, X } from 'lucide-react';
import { Input } from './input';
import { Badge } from './badge';

/**
 * ArticleAutocomplete - Real autocomplete pentru articole
 * Afiseaza sugestii instant pe masura ce tastezi
 * Suporta cautare dupa denumire furnizor cand supplier_id este furnizat
 */
export const ArticleAutocomplete = ({ 
  articles = [], 
  onSelect, 
  placeholder = "Cauta articol...",
  className = "",
  supplierId = null
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const justSelected = useRef(false);

  // Filter articles as user types
  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }
    if (inputValue.trim().length < 2) {
      setFilteredArticles([]);
      setShowSuggestions(false);
      return;
    }

    const query = inputValue.toLowerCase();
    const filtered = articles.filter(article => {
      if (article.name.toLowerCase().includes(query)) return true;
      if (article.code?.toLowerCase().includes(query)) return true;
      if (article.category_name?.toLowerCase().includes(query)) return true;
      // Search by supplier article name
      if (article.suppliers) {
        for (const s of article.suppliers) {
          if (supplierId && s.supplier_id === supplierId && s.supplier_article_name?.toLowerCase().includes(query)) return true;
          if (!supplierId && s.supplier_article_name?.toLowerCase().includes(query)) return true;
        }
      }
      return false;
    }).slice(0, 100);

    setFilteredArticles(filtered);
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  }, [inputValue, articles, supplierId]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectArticle = (article) => {
    justSelected.current = true;
    setInputValue(article.name);
    setShowSuggestions(false);
    setFilteredArticles([]);
    onSelect?.(article);
  };

  const handleClear = () => {
    setInputValue('');
    setFilteredArticles([]);
    setShowSuggestions(false);
    onSelect?.(null);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredArticles.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredArticles.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectArticle(filteredArticles[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => inputValue.length >= 2 && filteredArticles.length > 0 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-12 pr-10 h-14 text-lg"
          autoComplete="off"
        />
        {inputValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && filteredArticles.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-2xl border-2 border-blue-200 max-h-96 overflow-y-auto">
          <div className="p-2">
            {filteredArticles.map((article, index) => (
              <div
                key={article.id}
                onClick={() => handleSelectArticle(article)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  index === selectedIndex 
                    ? 'bg-blue-50 border-2 border-blue-400' 
                    : 'hover:bg-slate-50 border-2 border-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <p className="font-semibold text-base text-slate-900 truncate">
                        {article.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-slate-600 font-mono">{article.code}</p>
                      {article.category_name && (
                        <Badge variant="secondary" className="text-xs">
                          {article.category_name}
                        </Badge>
                      )}
                    </div>
                    {/* Show supplier-specific name if available */}
                    {supplierId && article.suppliers?.find(s => s.supplier_id === supplierId)?.supplier_article_name && (
                      <p className="text-xs text-amber-700 mt-1 truncate">
                        Furnizor: {article.suppliers.find(s => s.supplier_id === supplierId).supplier_article_name}
                      </p>
                    )}
                    {article.total_stock !== undefined && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        Stoc: {article.total_stock} {article.unit}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Footer with count */}
          <div className="px-4 py-2 bg-slate-50 border-t text-center">
            <p className="text-xs text-slate-600">
              {filteredArticles.length} rezultate gasite
            </p>
          </div>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && filteredArticles.length === 0 && inputValue.length >= 2 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-600 text-center">
            Niciun articol gasit pentru "{inputValue}"
          </p>
        </div>
      )}
    </div>
  );
};

export default ArticleAutocomplete;
